import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowCounterClockwiseIcon,
  CheckCircleIcon,
} from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { toast } from 'sonner';
import type { HarnessActionAppliedResult } from '@erp/shared-types';
import { orpc } from '@/api/rpc/client';
import { invalidateOrpcPaths } from '@/api/rpc/invalidateByPath';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { NavButtonToolResult } from '../NavButtonToolResult';
import { NamiActionThumbs } from './NamiActionThumbs';
import { NamiChangeList } from './namiChangeList';
import { NamiViaBadge } from './NamiViaBadge';

/**
 * A change the assistant applied directly: what changed, an Undo while the
 * window is open, a link to the record, and thumbs.
 */
export function NamiChangeCard({
  result,
}: {
  result: HarnessActionAppliedResult;
}) {
  const gt = useGT();
  const queryClient = useQueryClient();
  // Sampled once per mount: the card is either undoable or not for as long
  // as it is on screen, and render stays pure.
  const [mountedAt] = useState(() => Date.now());
  const [undone, setUndone] = useState<string | null>(null);
  const undoWindowOpen =
    result.undoAvailableUntil !== undefined &&
    new Date(result.undoAvailableUntil).getTime() > mountedAt;

  const undo = useMutation({
    ...orpc.harness.actions.undo.mutationOptions(),
    onSuccess: (outcome) => {
      invalidateOrpcPaths(queryClient, outcome.invalidates);
      if (outcome.status === 'applied') {
        setUndone(outcome.summary);
        toast.success(outcome.summary);
      } else {
        toast.error(outcome.error ?? outcome.summary);
      }
    },
    onError: (error) =>
      toast.error(
        error instanceof Error
          ? error.message
          : gt('Could not undo this change', { context: 'undo failure toast' }),
      ),
  });

  return (
    <Card className="flex flex-col gap-2 border-agent-border bg-agent-subtle p-3">
      <div className="flex items-start gap-2">
        <CheckCircleIcon
          className="mt-0.5 size-4 shrink-0 text-muted-foreground"
          weight="fill"
        />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="text-sm text-card-foreground">
            {undone ?? result.summary}
          </div>
          <NamiViaBadge />
        </div>
      </div>
      {!undone && result.changes && result.changes.length > 0 ? (
        <NamiChangeList changes={result.changes} />
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {result.entity?.path ? (
            <NavButtonToolResult
              to={result.entity.path}
              label={result.entity.label}
            />
          ) : null}
          {undoWindowOpen && !undone ? (
            <Button
              size="sm"
              variant="outline"
              disabled={undo.isPending}
              onClick={() => undo.mutate({ toolCallId: result.toolCallId })}
            >
              <ArrowCounterClockwiseIcon className="size-4" />
              <T context="undo button on an AI assistant change card">Undo</T>
            </Button>
          ) : null}
        </div>
        <NamiActionThumbs toolCallId={result.toolCallId} />
      </div>
    </Card>
  );
}
